import { useSelector } from "react-redux";
import { MaterialSelection } from "../../types/material";
import { RootStore } from "../../state/store";
import { Fragment, useMemo, useState } from "react";
import { StringInput } from "../Inputs";
import { PlusMinusButton } from "../PlusMinusButton";
import { BookQuickLinks, ChapterList } from "./ChapterSelector";
import { Row } from "../Row";
import { chapterSelected } from "../../util/generalUtil";

interface Player {
  name: string;
  material: MaterialSelection;
}
interface GameSettingProps<E> {
  title: string;
  description?: string;
  hasMaterial?: boolean;
  minQuizzers?: number;
  extraSettings?: {
    defaultSettings: E;
    settingsUI: (
      settings: E,
      setSettings: (newSettings: E) => void
    ) => React.ReactNode;
  };
  onStart: (players: Player[], settings: E) => void;
  onGoBack: () => void;
}

export function GameSettings<E>(props: GameSettingProps<E>) {
  const settings = useSelector((state: RootStore) => state.settings);
  const [players, setPlayers] = useState<Player[]>(
    settings.quizNowQuizzers.map((q) => ({
      name: q,
      material: settings.selectedChapters,
    }))
  );
  const [playerMaterialViewing, setPlayerMaterialViewing] = useState(-1);

  const [gameSettings, setGameSettings] = useState(
    props.extraSettings?.defaultSettings
  );

  const startEnabled = useMemo(() => {
    let newEnabled = true;
    players.forEach((quizzer) => {
      if (!quizzer.name) newEnabled = false;
    });
    if (props.minQuizzers && props.minQuizzers > players.length) return false;
    if (props.hasMaterial && players.some((p) => !chapterSelected(p.material)))
      return false;
    return newEnabled;
  }, [players, props.minQuizzers]);

  const h1 = (
    <h1
      style={{
        margin: 0,
        fontSize: 28,
        marginBottom: 12,
      }}
    >
      {props.title}
    </h1>
  );
  if (playerMaterialViewing >= 0) {
    return (
      <div className="settings-container">
        <div className="settings-top-sticky">
          {h1}
          <Row>
            <button
              className="back-button"
              onClick={() => setPlayerMaterialViewing(-1)}
              style={{ marginBottom: 10 }}
            >
              Go Back
            </button>
            <span
              style={{ marginLeft: 10, marginTop: 5 }}
            >{`Material for ${players[playerMaterialViewing].name}`}</span>
          </Row>
          <BookQuickLinks />
        </div>
        <ChapterList
          allowVerseSelection
          selected={players[playerMaterialViewing].material}
          onChange={(material) => {
            const newPlayers = [...players];
            newPlayers[playerMaterialViewing] = {
              ...players[playerMaterialViewing],
              material,
            };
            setPlayers(newPlayers);
          }}
        />
      </div>
    );
  }
  return (
    <div style={{ marginLeft: "auto", marginRight: "auto", maxWidth: 440 }}>
      {h1}
      {props.description && <p>{props.description}</p>}
      <div>
        {players.map((item, index) => (
          <Fragment key={`quizzer-${index}`}>
            <StringInput
              caption={`Quizzer ${index + 1}:`}
              value={item.name}
              onChange={(value) => {
                const result = [...players];
                result[index] = { ...item, name: value };
                setPlayers(result);
              }}
              afterInput={
                <div style={{ display: "flex", width: 40 }}>
                  {(index > 0 || players.length > 1) && (
                    <div style={{ marginLeft: 5 }}>
                      <PlusMinusButton
                        onClick={() => {
                          const newList = [...players];
                          newList.splice(index, 1);
                          setPlayers(newList);
                        }}
                      >
                        -
                      </PlusMinusButton>
                    </div>
                  )}
                  {players.length === index + 1 && (
                    <div style={{ marginLeft: 5 }}>
                      <PlusMinusButton
                        onClick={() => {
                          const newList = [...players];
                          newList.push({
                            name: "",
                            material: settings.selectedChapters,
                          });
                          setPlayers(newList);
                        }}
                      >
                        +
                      </PlusMinusButton>
                    </div>
                  )}
                </div>
              }
            />
            {props.hasMaterial && (
              <p>
                Select
                <button
                  className="link"
                  onClick={() => setPlayerMaterialViewing(index)}
                >
                  Material
                </button>
                {item.name ? ` for ${item.name}` : ""}
              </p>
            )}
          </Fragment>
        ))}
      </div>
      {gameSettings &&
        props.extraSettings?.settingsUI?.(gameSettings, setGameSettings)}
      <div style={{ display: "flex", marginTop: 15, marginBottom: 15 }}>
        <button
          className="back-button"
          onClick={props.onGoBack}
          style={{ marginRight: 15 }}
        >
          Go Back
        </button>
        <button
          className={startEnabled ? "clickable" : "disabled-clickable"}
          onClick={() => {
            if (!startEnabled) return;
            props.onStart(players, gameSettings!);
            window.scrollTo(0, 0);
          }}
        >
          Start
        </button>
      </div>
    </div>
  );
}
